<!--
/**
 * 用于逐步替代 Product 组件 - NewProduct
 *
 * 功能描述：
 * 1. 替代 Product 组件，使 其相关 数据功能都从外部获取
 *
 * Props
 * productList {Array} [[]] - 产品列表
 * loading {boolean} [false] - 是否处于 loading 请求
 * error {string} [null] - 错误信息
 *
 * 使用示例:
 * <Product :product-list="list" :loading="loading" :error="error" />
 *
 */
-->
<template>
  <ProductList
    :list="productList"
    :error="error"
    :loading="loading"
    @load="$emit('load')"
  >
    <template
      v-for="(item, index) in productList"
      :key="index"
    >
      <template v-if="item.type === 'ai-search-spot'">
        <H5AiSearchSpot
          :item="item"
          v-if="deviceType === 'MOBILE'"
        />
        <AiSearchSpot
          :item="item"
          v-else
        />
      </template>
      <div
        v-else
        class="product-item-block"
        :data-id="item.offerId"
      >
        <ProductItem
          :item="item"
          @on-click="(data) => emit('itemClick', data)"
        />
      </div>
    </template>
  </ProductList>
</template>

<script setup>
import { buriedPointUtil, throttle } from '@/utils/utils'
import { ProductItem } from '@/components/ProductItem'
import AiSearchSpot from './components/AiSearchSpot.vue'
import H5AiSearchSpot from './components/H5AiSearchSpot.vue'
import { useDevice } from '@/hooks/useDevice'
import ProductList from './ProductList.vue'

const props = defineProps({
  productList: {
    type: Array,
    required: true
  },
  loading: Boolean,
  error: {
    type: String,
    default: null
  }
})
const emit = defineEmits(['load', 'itemClick'])

const { deviceType } = useDevice()

watch(() => props.productList, (value, oldValue) => {
  if (!value.length) return

  nextTick(() => {
    setTimeout(() => {
      observerProduct(value.length - oldValue.length)
    }, 100)
  })
})

/**
 * 保存最后一页的商品卡片列表，用于判断是否触发 load 事件
 */
const lastElements = shallowRef([])
let observedLastLength = 0

/**
 * 保存待上报的已曝光列表
 */
const observedIdList = ref(new Set())

const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const offerId = entry.target.dataset.id
      observedIdList.value.add(offerId)

      if ([...lastElements.value].includes(entry.target)) {
        observedLastLength++
        if (observedLastLength > (lastElements.value.length / 2)) {
          /**
           * 列表过半触发 load 事件，并加载完后清除
           */
          emit('load')
          lastElements.value = []
          observedLastLength = 0
        }
      }
      reportExposure()
      observer.unobserve(entry.target)
    }
  })
}, { threshold: 0.51 })

const observerProduct = (length) => {
  const elements = [...document.querySelectorAll('.product-item-block')].slice(-length)

  /**
   * 重置 最后一页的商品卡片列表 和 状态
   */
  lastElements.value = elements
  observedLastLength = 0

  elements.forEach(product => {
    observer.observe(product)
  })
}

// 埋点上报
const reportExposure = throttle(() => {
  if (observedIdList.value.size === 0) return
  if (deviceType.value === 'MOBILE') {
    buriedPointUtil({ eventId: 'MPEX101', others: { offerIdList: [...observedIdList.value] } })
  } else {
    buriedPointUtil({ eventId: 'PEX101', others: { offerIdList: [...observedIdList.value] } })
  }
  /**
     * 上报完成后清除
     */
  observedIdList.value = new Set()
}, 500)
</script>

<style lang="less" scoped>
.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px 13px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px 16px;
  }
}
</style>
